import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { versionLabel, type DocumentClauseRef, type DocumentRegister, type DocumentRegisterEntry } from '@grc/types';
import { apiFetch, apiUrl } from '../../lib/api.js';
import { useAuth, useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  formatDate,
} from '../../components/ui.js';
import { registerKey } from './shared.js';
import './policies.css';

const ALL = '__all__';
type Show = 'all' | 'published' | 'unpublished';

const STATUS_TONE = { draft: 'info', approved: 'success', archived: 'neutral' } as const;

/** A same-ramp meter: the fill carries the state, the track is the soft step of that ramp. */
function Meter({ percent, state, label }: { percent: number; state: 'complete' | 'overdue' | 'progress'; label: string }) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div
      className={`register-meter register-meter-${state}`}
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className="register-meter-fill" style={{ width: `${clamped}%` }} />
    </div>
  );
}

function StatTile({ label, value, hint }: { label: string; value: ReactNode; hint: ReactNode }) {
  return (
    <div className="register-tile" role="listitem">
      <div className="register-tile-label">{label}</div>
      <div className="register-tile-value">{value}</div>
      <div className="register-tile-hint">{hint}</div>
    </div>
  );
}

function signatureState(s: { signed: number; total: number; overdue: number }): 'complete' | 'overdue' | 'progress' {
  if (s.overdue > 0) return 'overdue';
  if (s.total > 0 && s.signed === s.total) return 'complete';
  return 'progress';
}

/** One document's clause references, grouped by standard with Annex A controls marked. */
function ClauseRefs({ refs }: { refs: DocumentClauseRef[] }) {
  if (refs.length === 0) return <span className="register-muted">Not mapped</span>;
  const groups = new Map<string, DocumentClauseRef[]>();
  for (const ref of refs) {
    const list = groups.get(ref.standardLabel) ?? [];
    list.push(ref);
    groups.set(ref.standardLabel, list);
  }
  return (
    <ul className="register-clauses">
      {[...groups].map(([label, list]) => (
        <li key={label}>
          <span className="register-clauses-standard">{label}</span>
          <span className="register-clauses-list">
            {list.map((ref) => (
              <span
                key={ref.clauseId ?? `${ref.standardVersionId}:*`}
                className={`register-clause register-clause-${ref.kind}`}
                title={`${ref.clauseNumber ? `${ref.clauseNumber} ` : ''}${ref.title}${
                  ref.kind === 'annex' ? ' (Annex A control)' : ''
                } · ${ref.source === 'both' ? 'standard and crosswalk' : ref.source}`}
              >
                {ref.clauseNumber ?? 'Whole standard'}
              </span>
            ))}
          </span>
        </li>
      ))}
    </ul>
  );
}

function DocumentSignatures({ doc }: { doc: DocumentRegisterEntry }) {
  if (!doc.signatures || !doc.release) return <span className="register-muted">Not published for acceptance</span>;
  const s = doc.signatures;
  return (
    <div className="register-signed">
      <Meter percent={s.percent} state={signatureState(s)} label={`${doc.title}: ${s.signed} of ${s.total} signed`} />
      <span className="register-signed-text">
        <strong>{s.signed}</strong> signed · {s.notSigned} not signed
        {s.overdue > 0 && (
          <>
            {' '}
            <Badge tone="danger">{s.overdue} overdue</Badge>
          </>
        )}
      </span>
    </div>
  );
}

/**
 * The online Document Register: every controlled document against the clauses
 * and Annex A controls it supports, who has signed the policies published for
 * acceptance, an Excel download of the register with the signature log, and a
 * PDF of each document. Admins and reviewers (same audience as policy tracking).
 */
export function DocumentRegisterPage() {
  const { me } = useAuth();
  const { org } = useOrg();
  const isAdmin = org.role === 'admin' || (me?.user.isSystemAdmin ?? false);
  const canView = isAdmin || org.role === 'reviewer';

  const register = useQuery({
    queryKey: registerKey(org.orgId),
    queryFn: () => apiFetch<DocumentRegister>(`/orgs/${org.orgId}/document-register`),
    enabled: canView,
  });

  const [search, setSearch] = useState('');
  const [standard, setStandard] = useState(ALL);
  const [show, setShow] = useState<Show>('all');
  const [includeArchived, setIncludeArchived] = useState(false);

  const documents = useMemo(() => {
    const all = register.data?.documents ?? [];
    const needle = search.trim().toLowerCase();
    return all.filter((d) => {
      if (!includeArchived && d.status === 'archived') return false;
      if (show === 'published' && !d.release) return false;
      if (show === 'unpublished' && d.release) return false;
      if (standard !== ALL && !d.clauses.some((c) => c.standardVersionId === standard)) return false;
      if (needle) {
        const haystack = `${d.title} ${d.key} ${d.clauses.map((c) => c.clauseNumber ?? '').join(' ')}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [register.data, search, standard, show, includeArchived]);

  if (!canView) {
    return (
      <EmptyState
        title="Not available"
        message="The document register is available to organisation admins and reviewers."
      />
    );
  }
  if (register.isLoading) return <LoadingPane label="Loading the document register" />;
  if (register.isError || !register.data) {
    return (
      <div>
        <PageHeader title="Document register" />
        <EmptyState
          title="Could not load the document register"
          message="Something went wrong fetching the organisation's documents — please try again shortly."
        />
      </div>
    );
  }

  const data = register.data;
  const { summary } = data;
  const policies = data.documents.filter((d) => d.release && d.signatures);
  const staff = [...data.staff].sort((a, b) => a.percent - b.percent || a.name.localeCompare(b.name));

  return (
    <div>
      <PageHeader
        title="Document register"
        subtitle="Every controlled document, the clauses and Annex A controls it supports, and who has signed the policies published for acceptance."
        actions={
          <>
            <a href={apiUrl(`/orgs/${org.orgId}/document-register/export.xlsx`)}>
              <Button variant="secondary">Download Excel</Button>
            </a>
            <Link to="/policies/tracking">
              <Button variant="ghost">Policy acceptance</Button>
            </Link>
          </>
        }
      />

      <div className="register-tiles" role="list" aria-label="Register summary">
        <StatTile
          label="Documents"
          value={summary.documents}
          hint={`${summary.mapped} mapped to a standard${
            data.standards.length > 0 ? ` · ${data.standards.map((s) => s.label).join(', ')}` : ''
          }`}
        />
        <StatTile
          label="Policies published for acceptance"
          value={summary.policies}
          hint={
            summary.policies === 0
              ? 'Publish a corpus item for acceptance to start tracking signatures'
              : `${summary.policiesFullySigned} fully signed · ${summary.policiesNotFullySigned} not fully signed`
          }
        />
        <StatTile
          label="Signatures"
          value={
            <>
              {summary.signatures.signed}
              <span className="register-tile-of"> of {summary.signatures.total}</span>
            </>
          }
          hint={
            <>
              {summary.signatures.notSigned} not signed
              {summary.signatures.overdue > 0 && (
                <>
                  {' '}
                  <Badge tone="danger">{summary.signatures.overdue} overdue</Badge>
                </>
              )}
            </>
          }
        />
        <StatTile
          label="Staff who have signed every policy"
          value={`${summary.staff.percentFullySigned}%`}
          hint={`${summary.staff.fullySigned} of ${summary.staff.total} ${summary.staff.total === 1 ? 'person' : 'people'}`}
        />
      </div>

      {data.documents.length === 0 ? (
        <EmptyState
          title="No documents yet"
          message="Documents in the shared corpus appear here with the clauses they support and who has signed them."
        />
      ) : (
        <>
          <div className="policy-filters">
            <Input
              aria-label="Search documents"
              placeholder="Search title, key or clause…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 280 }}
            />
            <label>
              <span className="sr-only">Standard</span>
              <Select value={standard} onChange={(e) => setStandard(e.target.value)}>
                <option value={ALL}>All standards</option>
                {data.standards.map((s) => (
                  <option key={s.versionId} value={s.versionId}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </label>
            <label>
              <span className="sr-only">Show</span>
              <Select value={show} onChange={(e) => setShow(e.target.value as Show)}>
                <option value="all">All documents</option>
                <option value="published">Published for acceptance</option>
                <option value="unpublished">Not published for acceptance</option>
              </Select>
            </label>
            <label>
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
              />
              Include archived
            </label>
          </div>

          {documents.length === 0 ? (
            <EmptyState title="No matching documents" message="Nothing matches these filters." />
          ) : (
            <Table
              head={
                <>
                  <th>Document</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Clauses and Annex A controls</th>
                  <th>Signed</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </>
              }
            >
              {documents.map((d) => (
                <tr key={d.corpusItemId}>
                  <td>
                    <Link to={`/corpus/${d.corpusItemId}`} className="policy-row-title">
                      {d.title}
                    </Link>
                    <div className="policy-row-key">{d.key}</div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {d.currentVersion > 0 ? versionLabel(d.documentVersion, d.currentVersion) : '—'}
                    <span className="register-updated">
                      {formatDate(d.updatedAt)}
                      {d.updatedByName ? ` · ${d.updatedByName}` : ''}
                    </span>
                  </td>
                  <td>
                    <Badge tone={STATUS_TONE[d.status]}>{d.status}</Badge>
                    {d.release && (
                      <span className="policy-status-sub">
                        Published {versionLabel(d.release.documentVersion, d.release.versionNumber)} ·{' '}
                        {formatDate(d.release.publishedAt)}
                      </span>
                    )}
                  </td>
                  <td>
                    <ClauseRefs refs={d.clauses} />
                  </td>
                  <td>
                    <DocumentSignatures doc={d} />
                  </td>
                  <td className="register-actions">
                    <Link to={`/corpus/${d.corpusItemId}`}>Open</Link>
                    <a
                      href={apiUrl(`/corpus/${d.corpusItemId}/export.pdf`)}
                      aria-label={`Download ${d.title} as PDF`}
                      title="Download as PDF"
                    >
                      PDF
                    </a>
                  </td>
                </tr>
              ))}
            </Table>
          )}

          <div className="register-panels">
            <Card title="Policies signed">
              {policies.length === 0 ? (
                <p className="register-muted">No policies are published for acceptance.</p>
              ) : (
                <ul className="register-list">
                  {policies.map((d) => {
                    const s = d.signatures!;
                    return (
                      <li key={d.corpusItemId}>
                        <div className="register-list-head">
                          <Link to={`/corpus/${d.corpusItemId}`}>{d.title}</Link>
                          <span className="register-list-figure">
                            {s.signed} signed · {s.notSigned} not signed
                          </span>
                        </div>
                        <Meter
                          percent={s.percent}
                          state={signatureState(s)}
                          label={`${d.title}: ${s.signed} of ${s.total} signed`}
                        />
                        <div className="register-list-sub">
                          {versionLabel(d.release!.documentVersion, d.release!.versionNumber)} · {s.percent}% of{' '}
                          {s.total}{' '}
                          {s.total === 1 ? 'person' : 'people'}
                          {s.overdue > 0 && (
                            <>
                              {' '}
                              <Badge tone="danger">{s.overdue} overdue</Badge>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
            <Card title="Staff who have signed">
              {staff.length === 0 ? (
                <p className="register-muted">No members in scope.</p>
              ) : summary.policies === 0 ? (
                <p className="register-muted">
                  {staff.length} {staff.length === 1 ? 'person is' : 'people are'} in scope; nothing is published for
                  acceptance yet.
                </p>
              ) : (
                <ul className="register-list">
                  {staff.map((m) => (
                    <li key={m.userId}>
                      <div className="register-list-head">
                        <span>
                          {m.name} <Badge>{m.role}</Badge>
                        </span>
                        <span className="register-list-figure">{m.percent}%</span>
                      </div>
                      <Meter
                        percent={m.percent}
                        state={signatureState(m)}
                        label={`${m.name}: ${m.signed} of ${m.total} policies signed`}
                      />
                      <div className="register-list-sub">
                        {m.signed} of {m.total} {m.total === 1 ? 'policy' : 'policies'} signed
                        {m.overdue > 0 && (
                          <>
                            {' '}
                            <Badge tone="danger">{m.overdue} overdue</Badge>
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
